import { FocusTrap, inertHelper, isMobile, ScrollLock, Swipeable, SwipeDirection } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';
import { Collapse } from 'react-collapse';

import { getTranslation, keys } from '../../../Resources/Translations';
import { LinkViewModel } from '../../Common/LinkViewModel.csharp';
import { NavigationTrackerButton } from '../NavigationTracker/NavigationTrackerButton';
import { NavigationTrackerLink } from '../NavigationTracker/NavigationTrackerLink';
import { LoginMenuSection } from './Sections/LoginMenuSection';
import { LogoutMenuSection } from './Sections/LogoutMenuSection';
import { useBankIdSession } from './useBankIdSession';

interface ActionMenuProps {
    isActive: boolean;
    isLoggedIn: boolean;
    loginUrl: string;
    logoutUrl: string;
    registerUrl: string;
    firstName: string;
    lastName: string;
    setActionMenuOpen: (value: boolean) => void;
    isPhysicalStoreAdmin: boolean;
    isInPhysicalStoreMode: boolean;
    onPhysicalStoreChange: () => void;
    profileLinks: LinkViewModel[];
    rxSessionCookieName: string;
    rxSessionValidityCookieName: string;
    rxSessionExpirationTimeCookieName: string;
    showButtonLabel: boolean;
    buttonLabel: string;
}

export const ActionMenu = (props: ActionMenuProps) => {
    const { isActive, isLoggedIn, loginUrl, logoutUrl, registerUrl, firstName, lastName, showButtonLabel, buttonLabel, setActionMenuOpen } = props;
    const [isMobileVersion, setIsMobileVersion] = React.useState(isMobile());
    const actionMenuWrapperRef = React.useRef<HTMLDivElement>(null);
    const bankIdSession = useBankIdSession(props.rxSessionCookieName, props.rxSessionValidityCookieName, props.rxSessionExpirationTimeCookieName);

    const resizeEventHandler = () => {
        setIsMobileVersion(isMobile());
    };

    React.useEffect(() => {
        window?.addEventListener('resize', resizeEventHandler);

        return () => {
            window?.removeEventListener('resize', resizeEventHandler);
        };
    }, []);

    React.useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (actionMenuWrapperRef.current && !actionMenuWrapperRef.current.contains(e.target as Node) && isActive) {
                setActionMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [actionMenuWrapperRef, isActive]);

    const handleSwipe = (swipeDirection: SwipeDirection) => {
        if (swipeDirection === SwipeDirection.Left && isActive) {
            setActionMenuOpen(false);
        }
    };

    return (
        <Swipeable onSwipe={handleSwipe}>
            <FocusTrap enabled={isActive}>
                <div className="ActionMenuWrapper" ref={actionMenuWrapperRef}>
                    <NavigationTrackerButton
                        className={classNames('Header__ActionsMyProfile', { 'Header__ActionsMyProfile--LabelVisible': showButtonLabel })}
                        onClick={() => setActionMenuOpen(!isActive)}
                        aria-haspopup="true"
                        aria-expanded={isActive}
                        source="ActionMenu"
                    >
                        {showButtonLabel && <span className="hideOnMobile">{buttonLabel}</span>}
                        <span className="visuallyHidden">{getTranslation(keys['actionMenu.myPage'])}</span>
                    </NavigationTrackerButton>
                    <div
                        className={classNames('ActionMenu__Overlay', isActive && isMobileVersion && 'ActionMenu__Overlay--Active')}
                        onClick={() => setActionMenuOpen(false)}
                    />
                    <div className={classNames('ActionMenu', isActive && 'ActionMenu--Active')} {...inertHelper(isActive)}>
                        {isActive && isMobileVersion && (
                            <button className="ActionMenu__CloseButton" onClick={() => setActionMenuOpen(false)} aria-haspopup="true" aria-expanded={isActive}>
                                <span className="visuallyHidden">{getTranslation(keys['navigation.close'])}</span>
                            </button>
                        )}
                        <ScrollLock isLocked={isActive && isMobileVersion} />

                        <Collapse isOpened={isActive}>
                            <div className="ActionMenu__Placeholder">
                                <div className="ActionMenu__Content">
                                    <LoginMenuSection
                                        isLoggedIn={isLoggedIn}
                                        firstName={firstName}
                                        lastName={lastName}
                                        loginUrl={loginUrl}
                                        registerUrl={registerUrl}
                                        isPhysicalStoreAdmin={props.isPhysicalStoreAdmin}
                                        isInPhysicalStoreMode={props.isInPhysicalStoreMode}
                                        onPhysicalStoreChange={props.onPhysicalStoreChange}
                                        id="actionMenu"
                                    />

                                    <ul className="ActionMenu__LinksList">
                                        {props.profileLinks?.map((link) => (
                                            <li className="ActionMenu__Link" key={link.href}>
                                                <NavigationTrackerLink href={link.href} rel="nofollow" source="ActionMenu">
                                                    <span>{link.text}</span>
                                                </NavigationTrackerLink>
                                            </li>
                                        ))}
                                    </ul>

                                    <LogoutMenuSection isLoggedIn={isLoggedIn} logoutUrl={logoutUrl} hasActiveBankIdSession={bankIdSession.isValid} />
                                </div>
                            </div>
                        </Collapse>
                    </div>
                </div>
            </FocusTrap>
        </Swipeable>
    );
};
